import React, { useRef, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
    Grid,
    Column,
    Container,
    SectionTitle,
    Section,
    PlansGradient,
    LegalLinks,
    Accordion,
    Typography,
} from 'components';

import analytics from 'tracking/analytics';
import HeroBackground from 'assets/images/heroes/newyear_web.jpg';
import ConversionBackground from 'assets/images/heroes/newyear_web_2.jpg';

import { PATHS, URLS } from 'config';
import { PLANS_LIST__BUTTON__SELECT_PLAN } from 'tracking/trackingData';

import { StyledLink } from 'components/StyledCommon';
import { sendNewYearPageView } from 'tracking/sendTrackingEvent';
import {
    isTabletOrSmaller,
    route_with_params,
    useEffectOnlyOnce,
    useGuardNewYear,
    getNavBarHeight,
} from 'helpers';

import { Sections, Ratings } from './sections';
import {
    DesktopHero,
    SectionPlans,
    ColumnHeaders,
    ColumnPlans,
    MobileHero,
    Header,
    Description,
    LearnMoreLink,
    SectionConversion,
    LegalLinksConversion,
} from './styled';

const Page = () => {
    const history = useHistory();
    const { plans } = useGuardNewYear();
    const context = plans.map((p) => p.id).join(',');

    const onPlanClick = (e: MouseEvent<HTMLDivElement>, plan: any) => {
        e && e.preventDefault();

        analytics.trackEvent({
            ...PLANS_LIST__BUTTON__SELECT_PLAN,
            context: context,
            name: plan.id,
        });

        history.push(
            route_with_params(PATHS.newYearCheckout, {
                id: plan.id,
            }),
        );
    };

    const scrollRef = useRef<HTMLDivElement>(null);

    const onLearnMoreClick = () => {
        if (scrollRef.current) {
            window.scrollTo({
                behavior: 'smooth',
                top: scrollRef.current.offsetTop - getNavBarHeight(),
            });
        }
    };

    const isLicensed = plans && plans.some((p) => p.licensed);

    useEffectOnlyOnce(() => {
        plans && plans.length && sendNewYearPageView();
    });

    return plans && plans.length ? (
        <React.Fragment>
            <MobileHero $bg={HeroBackground} />
            <DesktopHero $bg={HeroBackground}>
                <SectionPlans>
                    <ColumnHeaders>
                        <Typography
                            variant="h4"
                            color="premiumPlus"
                            transform="uppercase"
                            spaceBottom="small"
                        >
                            <Trans>3…2…1… Play!</Trans>
                        </Typography>

                        <Header
                            variant="h2"
                            color="white"
                            transform="none"
                            fontWeight={500}
                        >
                            <Trans>New Year's Resolution Sale</Trans>
                        </Header>
                        <Header
                            variant="h2"
                            color="brand"
                            transform="uppercase"
                            fontWeight={500}
                        >
                            <Trans>30% OFF</Trans>
                        </Header>
                        <Description variant="bodyl" color="white">
                            <Trans>
                                Ready to master your instrument in 2022? Reach
                                your musical goals with unlimited access to
                                thousands of interactive songs and lessons.
                            </Trans>
                        </Description>

                        <LearnMoreLink onClick={onLearnMoreClick}>
                            <Trans>Learn more</Trans>
                        </LearnMoreLink>
                    </ColumnHeaders>

                    <ColumnPlans>
                        <PlansGradient
                            showToggle={false}
                            plans={plans}
                            onPlanClick={onPlanClick}
                            planCta={i18n._(t`Start playing now`)}
                            featuresType="blackFriday"
                        />

                        <LegalLinks
                            theme={isTabletOrSmaller() ? 'dark' : 'light'}
                        />
                    </ColumnPlans>
                </SectionPlans>
            </DesktopHero>

            <div ref={scrollRef}>
                <Sections isLicensed={isLicensed} />
            </div>

            {isLicensed && (
                <SectionConversion $bg={ConversionBackground}>
                    <Container>
                        <Grid alignCenter>
                            <Column cols={12} laptop={9} desktop={8}>
                                <Typography
                                    spaceTop="xgiant"
                                    align="center"
                                    variant="h4"
                                    color="premiumPlus"
                                    transform="uppercase"
                                >
                                    <Trans>3…2…1… Play!</Trans>
                                </Typography>
                                <Typography
                                    align="center"
                                    variant="h2"
                                    color="white"
                                    transform="none"
                                    fontWeight={500}
                                >
                                    <Trans>New Year's Resolution Sale</Trans>
                                </Typography>
                                <Typography
                                    variant="h2"
                                    color="brand"
                                    transform="uppercase"
                                    fontWeight={500}
                                    align="center"
                                    spaceBottom="xgiant"
                                >
                                    <Trans>30% OFF</Trans>
                                </Typography>
                            </Column>
                        </Grid>

                        <PlansGradient
                            biggerCta
                            showToggle={false}
                            plans={plans}
                            onPlanClick={onPlanClick}
                            planCta={i18n._(t`Start playing now`)}
                            featuresType="blackFriday"
                        />
                    </Container>

                    <LegalLinksConversion />
                </SectionConversion>
            )}

            <Ratings />

            <Section background="white">
                <Container>
                    <SectionTitle
                        align="center"
                        title={i18n._(t`Frequently asked questions`)}
                    />

                    <Accordion>
                        <div data-label={i18n._(t`What is Yousician?`)}>
                            <Trans>
                                As the largest music educator in the world,
                                Yousician has helped over 25 million people in
                                154 countries learn guitar, bass, piano,
                                ukulele, and vocals. Boasting thousands of
                                teacher-crafted lessons, exercises, and songs,
                                Yousician makes achieving your musical goals
                                manageable and fun. Thanks to award-winning
                                audio technology and world-class educators,
                                Yousician provides meaningful, real-time
                                feedback as you play.
                            </Trans>
                        </div>

                        {isLicensed ? (
                            <div
                                data-label={i18n._(
                                    t`What is Yousician Premium+?`,
                                )}
                            >
                                <Trans>
                                    Premium+ unlocks everything Yousician has to
                                    offer. Guided lessons, access to our entire
                                    library of thousands of songs, exercises,
                                    and teacher-crafted lessons. You also get
                                    instant access to our full instrument
                                    offering, allowing you to jump between
                                    instruments as you please.
                                </Trans>
                            </div>
                        ) : (
                            <div
                                data-label={i18n._(
                                    t`What is Yousician Premium All?`,
                                )}
                            >
                                <Trans>
                                    Premium All unlocks everything Yousician has
                                    to offer. Guided lessons, access to our
                                    entire library of thousands of songs,
                                    exercises, and teacher-crafted lessons. You
                                    also get instant access to our full
                                    instrument offering, allowing you to jump
                                    between instruments as you please.
                                </Trans>
                            </div>
                        )}

                        <div
                            data-label={i18n._(t`How does the discount work?`)}
                        >
                            <Trans>
                                The discount is applied only for the first year.
                                Your contract will renew automatically at the
                                regular price on your yearly renewal date, until
                                you cancel your subscription. You can cancel the
                                renewal of your subscription by logging into
                                your Yousician account{' '}
                                <StyledLink
                                    $size="bodym"
                                    $color="brand"
                                    href={'/login'}
                                >
                                    here
                                </StyledLink>
                                .
                            </Trans>
                        </div>

                        <div
                            data-label={i18n._(t`Am I eligible for the offer?`)}
                        >
                            <Trans>
                                All users who don't have an ongoing subscription
                                are eligible for the offer. If you have an
                                ongoing subscription with Yousician (with either
                                iOS, Google Play, card or PayPal) you might not
                                be eligible for the offer. Please log in to your
                                account to check if you're eligible for the
                                offer.
                            </Trans>
                        </div>

                        <div
                            data-label={i18n._(
                                t`What plans can I purchase with the discount?`,
                            )}
                        >
                            {isLicensed ? (
                                <Trans>
                                    The offer is available for yearly Premium+
                                    subscriptions only and does not apply for
                                    other premium options.
                                </Trans>
                            ) : (
                                <Trans>
                                    The offer is available for yearly Premium
                                    All subscriptions only and does not apply
                                    for other premium options.
                                </Trans>
                            )}
                        </div>

                        <div data-label={i18n._(t`Other questions`)}>
                            <Trans>
                                Feel free to reach out to us{' '}
                                <StyledLink
                                    $size="bodym"
                                    $color="brand"
                                    href={URLS.supportNew}
                                >
                                    here
                                </StyledLink>
                                . Our Customer Support team is always happy to
                                help &lt;3
                            </Trans>
                        </div>
                    </Accordion>
                </Container>
            </Section>
        </React.Fragment>
    ) : null;
};

/**
 * Name of the access campaign. This is being
 * used in order to display the login
 * page with benefits.
 */
Page.accessCampaign = 'new-year';

export default observer(Page);
